import { CLIENT_ID_ENV, DEFAULT_CLIENT_ID, resolveClientId } from "./clientId";
import { envValue, type EnvRecord } from "./envPreflight";

/**
 * TA-1336. The shop's name as a customer reads it, one per deployment.
 *
 * Kabir, 2026-10-14, typed: "this whole project is no longer mike specific but
 * multi tenant more, mike is just who were building it for, after we are going
 * to use it for other jewelers", then "the real blcokers just deal and fix and
 * cater the software". The deployment model ruled with it is ONE DEPLOYMENT PER
 * SHOP, so the name is a property of the process, read from one variable.
 *
 * Why an env value and not the `clients` row or the client_config artifact:
 * `clients.name` holds the legal name ("Nekta Jewelers"), which does not read
 * as "Nekta's" in a sentence, and it never reached the reply agent (item 54's
 * own note). The client_config artifact is read from the database per request
 * and has no name field; adding one is a T2 schema change nobody ruled on.
 * FINDINGS: the coordinator logs this choice.
 *
 * THE RULE. `ACKRA_SHOP_DISPLAY_NAME` set: validated and used. Unset: "Nekta"
 * when this deployment's client is cl_nekta (byte-identical to the line Kabir
 * approved, item 54), and a REFUSAL for any other client (section 0: a second
 * shop's bot must never call itself Nekta's).
 */

export const SHOP_DISPLAY_NAME_ENV = "ACKRA_SHOP_DISPLAY_NAME";

/** cl_nekta's display name, the one written into item 54's approved line. */
export const DEFAULT_SHOP_DISPLAY_NAME = "Nekta";

/** Letters, then letters, spaces, apostrophes or ampersands, ending on a letter. Forty at most. */
const SHAPE = /^[A-Za-z](?:[A-Za-z '&]{0,38}[A-Za-z])?$/;

/**
 * A problem sentence for a malformed name, or null. Never contains the value:
 * `envPreflight`'s renderer adds it.
 *
 * No digit, ever: the disclosure line carries the name, and a digit in a
 * customer-facing line is a figure the verifier must find in the KB
 * (section 4), so a name with one would hold every disclosure.
 */
export function validateShopDisplayName(value: string): string | null {
  if (SHAPE.test(value) && !/\s{2}/.test(value)) return null;
  return (
    "is not a shop display name. It is the name a customer reads in \"I'm <name>'s AI assistant\": " +
    "letters, single spaces, apostrophes and ampersands, starting and ending on a letter, 40 characters " +
    "at most, and no digit (a digit is a figure the verifier would hold)"
  );
}

export type ShopDisplayNameResolution =
  | { readonly ok: true; readonly name: string; readonly defaulted: boolean }
  | { readonly ok: false; readonly reason: string };

/** The refusal for a client other than cl_nekta with no name set. Ends without a full stop (preflight style). */
function missingNameReason(clientId: string): string {
  return (
    `${CLIENT_ID_ENV} is ${clientId}, not ${DEFAULT_CLIENT_ID}, and only ${DEFAULT_CLIENT_ID} may default to ` +
    `"${DEFAULT_SHOP_DISPLAY_NAME}": the AI disclosure line would tell this shop's customers they are talking ` +
    `to ${DEFAULT_SHOP_DISPLAY_NAME}'s assistant. Set ${SHOP_DISPLAY_NAME_ENV} to the shop's name as customers know it`
  );
}

/** The name for `clientId` from `env`, or why there is none. The one reader of ACKRA_SHOP_DISPLAY_NAME. */
export function resolveShopDisplayName(env: EnvRecord, clientId: string): ShopDisplayNameResolution {
  const raw = envValue(env, SHOP_DISPLAY_NAME_ENV);
  if (raw !== undefined) {
    const complaint = validateShopDisplayName(raw);
    if (complaint !== null) return { ok: false, reason: `${SHOP_DISPLAY_NAME_ENV}="${raw}" ${complaint}.` };
    return { ok: true, name: raw, defaulted: false };
  }
  if (clientId === DEFAULT_CLIENT_ID) return { ok: true, name: DEFAULT_SHOP_DISPLAY_NAME, defaulted: true };
  return { ok: false, reason: `${SHOP_DISPLAY_NAME_ENV} is not set. ${missingNameReason(clientId)}.` };
}

/**
 * The preflight's `requiredWhen` for ACKRA_SHOP_DISPLAY_NAME: null for cl_nekta
 * (and when the client id itself is refused, which its own entry reports),
 * the reason for any other client.
 */
export function shopDisplayNameRequiredReason(env: EnvRecord): string | null {
  const client = resolveClientId(env);
  if (!client.ok || client.clientId === DEFAULT_CLIENT_ID) return null;
  return missingNameReason(client.clientId);
}

/**
 * The name this process's deployment answers as, from `env` (the process
 * environment by default), through the two resolvers. Throws the resolver's
 * sentence on a misconfigured deployment, so a reader of the registry or the
 * reply agent fails loudly rather than calling a second shop "Nekta".
 * Not memoised: it is a regex and a string, and a test can stub the env.
 */
export function deploymentShopDisplayName(env: EnvRecord = process.env): string {
  const client = resolveClientId(env);
  if (!client.ok) throw new Error(client.reason);
  const name = resolveShopDisplayName(env, client.clientId);
  if (!name.ok) throw new Error(name.reason);
  return name.name;
}

/**
 * Decision list item 54 (a), Kabir's yes, 2026-09-29: the honest answer when a
 * customer asks whether they are talking to a bot. His text, verbatim, with
 * only the shop's name made a slot (TA-1336). For "Nekta" it is byte-identical
 * to the line he approved. Throws on a name the validator refuses, so a
 * malformed name can never reach a customer through this function.
 */
export function aiDisclosureLine(shop: string): string {
  const complaint = validateShopDisplayName(shop);
  if (complaint !== null) throw new Error(`aiDisclosureLine: "${shop}" ${complaint}.`);
  return `Fair question, I'm ${shop}'s AI assistant. If you'd rather talk to someone on the team, just say so.`;
}
